import {useContext, useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {DDEXPageContext} from './DDEXMainPage';
import {Button, Form, message, Modal, Space, Spin, Table, Typography} from 'antd';
import {CheckCircleOutlined, CloseCircleOutlined, PlusOutlined, SyncOutlined} from '@ant-design/icons';
import {artistOptionsAtom, defaultSelectionsAtom} from './ddex_atoms';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {useAtom, useAtomValue} from 'jotai';
import {uploadedFilesAtom} from './FileUploadStep';
import SortableTrackList from './SortableTrackList';
import axiosApiInstance from '../../../api/axiosClient';
import {artTracksFormAtom, releaseInfoFormAtom, useArtTracksForm, useReleaseInfoForm} from './release_form_atoms';
import {
    buildNewTrackAutofill,
    releaseMainArtistsForTrack,
    resolveReleaseInfoForTrackAutofillAsync,
} from './trackAutofill';

dayjs.extend(duration);

const formatDuration = (duration: number) => {
    // Extract whole minutes
    const minutes = Math.floor(duration / 60);

    // Extract remaining whole seconds
    const seconds = Math.floor(duration % 60);

    // Extract milliseconds
    const milliseconds = Math.round((duration % 1) * 1000);

    // Format the result as mm:ss:SSS
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMilliseconds = String(milliseconds).padStart(3, '0');

    return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};

// Helper function to clean filename for matching
const cleanNameForMatching = (name) => {
    // Remove file extension and special characters for better matching
    return name
        .replace(/\.[^/.]+$/, "") // remove file extension
        .toLowerCase()
        .replace(/[\s-_]+/g, " ") // normalize spaces and separators
        .trim();
};

// Function to find the best match between track name and filename
const findBestMatch = (trackName, audioFiles) => {
    const cleanTrackName = cleanNameForMatching(trackName);

    // Find exact match first
    for (const file of audioFiles) {
        const cleanFileName = cleanNameForMatching(file.name);
        if (cleanFileName === cleanTrackName) {
            return file.name;
        }
    }

    // Find partial match if no exact match
    for (const file of audioFiles) {
        const cleanFileName = cleanNameForMatching(file.name);
        if (cleanFileName.includes(cleanTrackName) || cleanTrackName.includes(cleanFileName)) {
            return file.name;
        }
    }

    // Return null if no match found
    return null;
};

interface VolumeData {
    volumeNumber: number;
    tracks: API.MusicDistribution.ArtTrack[];
}

const StepsFormSecondPage = ({edit}: { edit: boolean }) => {
    const location = useLocation();
    const id = location.pathname.split('?')[0].split('/').filter(Boolean).pop() || '';
    const {firstFormPageRef, secondFormPageRef, listRef} = useContext(DDEXPageContext);
    const [removingFiles, setRemovingFiles] = useState([]);
    const [errorRemovingFiles, setErrorRemovingFiles] = useState([]);
    const [defaultSelections] = useAtom(defaultSelectionsAtom);
    const [uploadedFiles] = useAtom(uploadedFilesAtom);
    const artistOptions = useAtomValue(artistOptionsAtom);
    const [volumes, setVolumes] = useState<VolumeData[]>([]);
    const [loading, setLoading] = useState(false);
    const [tracks, setTracks] = useState<API.MusicDistribution.ArtTrack[]>([]);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewData, setPreviewData] = useState<{
        matched: { track: API.MusicDistribution.ArtTrack, filename: string }[];
        unmatched: API.MusicDistribution.ArtTrack[];
    }>({matched: [], unmatched: []});

    // State for create tracks preview
    const [isCreateTracksPreviewVisible, setIsCreateTracksPreviewVisible] = useState(false);
    const [createTracksPreviewData, setCreateTracksPreviewData] = useState<{
        filesToCreate: { filename: string, title: string }[];
    }>({filesToCreate: []});

    // Form instances from jotai atoms
    const releaseInfoForm = useAtomValue(useReleaseInfoForm);
    const artTracksForm = useAtomValue(useArtTracksForm);
    const [releaseInfoData] = useAtom(releaseInfoFormAtom);
    const [artTracksData, setArtTracksData] = useAtom(artTracksFormAtom);

    // Filter only audio files for selection
    const audioFiles = uploadedFiles.filter(file => file.fileType === 'audio');

    // Fetch tracks and placements for the release
    const fetchTracksAndPlacements = async () => {
        // Get the release ID from the URL path instead of the form
        const releaseId = id;

        if (!releaseId) {
            // If there's no release ID or UPC, initialize with empty volumes
            setVolumes([{volumeNumber: 1, tracks: []}]);
            return;
        }

        try {
            setLoading(true);
            const response = await axiosApiInstance.get(`/api/products/art_tracks/`,
                {params: {release_id: releaseId}});
            const tracksData = response.data;
            console.log('tracksData', tracksData);
            if (tracksData) {
                // Update the tracks state with the fetched tracks
                setTracks(tracksData);

                // Update the form with the fetched tracks
                artTracksForm.setFieldsValue({
                    art_tracks: tracksData
                });
                // Update the atom with the new data
                setArtTracksData({...artTracksData, art_tracks: tracksData});

                // Update volumes from the fetched tracks
                updateVolumesFromTracks(tracksData);
                // message.success('Tracks loaded successfully');
            } else {
                // Initialize with empty volumes if no tracks found
                setVolumes([{volumeNumber: 1, tracks: []}]);
                setTracks([]);

                // Update the form with empty tracks
                artTracksForm.setFieldsValue({
                    art_tracks: []
                });
                // Update the atom with the new data
                setArtTracksData({...artTracksData, art_tracks: []});
            }
        } catch (error) {
            console.error('Error fetching tracks:', error);
            message.error('Failed to load tracks');
            // Initialize with empty volumes on error
            setVolumes([{volumeNumber: 1, tracks: []}]);
        } finally {
            setLoading(false);
        }
    };

    // Initialize volumes from tracks
    useEffect(() => {
        // Fetch tracks and placements when the component mounts
        fetchTracksAndPlacements();
    }, [releaseInfoForm, releaseInfoData]);

    // Update volumes when tracks change
    useEffect(() => {
        if (tracks.length > 0 && volumes.length === 0) {
            updateVolumesFromTracks(tracks);
        }
    }, [tracks]);
    // useEffect(() => {
    //      console.log({id})
    // }, [id]);

    // Generate preview data for auto-sync
    const generateAutoSyncPreview = () => {
        const matched = [];
        const unmatched = [];

        tracks.forEach((track, index) => {
            const trackName = track.title || `Track ${index + 1}`;
            const matchedFilename = findBestMatch(trackName, audioFiles);

            if (matchedFilename) {
                matched.push({
                    track,
                    filename: matchedFilename
                });
            } else {
                unmatched.push(track);
            }
        });

        return {matched, unmatched};
    };

    // Apply auto-sync changes after confirmation
    const applyAutoSync = async () => {
        const {matched} = previewData;

        if (matched.length === 0) {
            message.info('No tracks were matched with audio files');
            return;
        }

        // Show loading message
        const loadingMessage = message.loading('Updating tracks...', 0);
        let successCount = 0;
        let errorCount = 0;

        try {
            const updatedTracks = [...tracks]; // Create a copy to update

            // Process each matched track
            for (const matchItem of matched) {
                const trackIndex = updatedTracks.findIndex(t => t.id === matchItem.track.id);
                if (trackIndex === -1) continue;

                // Find the matching file to get its duration
                const audioFile = audioFiles.find(file => file.name === matchItem.filename);
                const trackToUpdate = {
                    ...updatedTracks[trackIndex],
                    filename: matchItem.filename,
                    is_uploaded: true
                };

                // Set duration if available
                if (audioFile?.duration) {
                    trackToUpdate.duration = audioFile.duration; // Assuming duration is in nanoseconds
                }

                try {
                    // Update the track in the database
                    const response = await axiosApiInstance.put('/api/products/art_tracks/', trackToUpdate,
                        {params: {without_update_artists: true}});

                    // Update the track in our local array with the response data
                    if (response.data && response.data.track) {
                        updatedTracks[trackIndex] = response.data.track;
                    } else {
                        updatedTracks[trackIndex] = trackToUpdate;
                    }

                    successCount++;
                } catch (error) {
                    console.error(`Error updating track ${trackToUpdate.id}:`, error);
                    errorCount++;
                    // Still update the track in our local array even if the API call fails
                    updatedTracks[trackIndex] = trackToUpdate;
                }
            }

            // Update tracks state
            setTracks(updatedTracks);

            // Update the form with the updated tracks
            artTracksForm.setFieldsValue({
                art_tracks: updatedTracks
            });

            // Update the atom with the new data
            setArtTracksData({...artTracksData, art_tracks: updatedTracks});

            // Update volumes state
            updateVolumesFromTracks(updatedTracks);

            // Close the preview modal
            setIsPreviewModalVisible(false);

            // Show success/error message
            loadingMessage();
            if (errorCount === 0) {
                message.success(`Auto-synced ${successCount} of ${tracks.length} tracks`);
            } else {
                message.warning(`Auto-synced ${successCount} tracks, but failed to update ${errorCount} tracks in the database`);
            }
        } catch (error) {
            loadingMessage();
            console.error('Error in auto-sync process:', error);
            message.error('Failed to auto-sync tracks');
        }
    };

    // Show preview before auto-sync
    const handleAutoSync = () => {
        const preview = generateAutoSyncPreview();
        setPreviewData(preview);
        setIsPreviewModalVisible(true);
    };

    // Generate preview data for creating tracks from audio files
    const generateCreateTracksPreview = () => {
        const existingFilenames = tracks.map(track => track.filename);

        // Filter audio files that aren't already assigned to tracks
        const unassignedAudioFiles = audioFiles.filter(file =>
            !existingFilenames.includes(file.name)
        );

        // Generate preview data
        const filesToCreate = unassignedAudioFiles.map(file => {
            // Generate a clean title from the filename
            const title = file.name
                .replace(/\.[^/.]+$/, "") // remove file extension
                .replace(/[-_]/g, " "); // replace dashes and underscores with spaces

            return {
                filename: file.name,
                title
            };
        });

        return {filesToCreate};
    };

    // Show preview before creating tracks
    const handleCreateTracksFromAudio = () => {
        const preview = generateCreateTracksPreview();

        if (preview.filesToCreate.length === 0) {
            message.info('All audio files are already assigned to tracks');
            return;
        }

        setCreateTracksPreviewData(preview);
        setIsCreateTracksPreviewVisible(true);
    };

    // Apply create tracks after confirmation
    const applyCreateTracks = async () => {
        const {filesToCreate} = createTracksPreviewData;

        if (filesToCreate.length === 0) {
            message.info('No files to create tracks from');
            return;
        }

        // Get the release ID from the URL path instead of the form
        const releaseId = id || '';

        // Show loading message
        const loadingMessage = message.loading('Creating tracks...', 0);

        try {
            const newTracks = [];
            const releaseInfo = await resolveReleaseInfoForTrackAutofillAsync(
                releaseInfoForm?.getFieldsValue?.() ?? {},
                releaseInfoData ?? {},
                releaseId,
            );
            const releaseMainArtists = releaseMainArtistsForTrack(releaseInfo.artists);

            // Create new tracks for each unassigned audio file using backend API
            for (let i = 0; i < filesToCreate.length; i++) {
                const fileInfo = filesToCreate[i];
                const file = audioFiles.find(f => f.name === fileInfo.filename);

                if (!file) continue;

                const {title: autofillTitle} = buildNewTrackAutofill(releaseInfo);

                // Prepare track data
                const trackData = {
                    duration: undefined,
                    // ...defaultSelections,
                    title: autofillTitle || fileInfo.title,
                    filename: fileInfo.filename,
                    is_uploaded: true,
                    volume_number: 1,
                    track_number: i + 1,
                    audio_file_release_id: releaseId,
                    main_artists: releaseMainArtists,
                };

                // Set duration if available
                if (file.duration) {
                    trackData.duration = file.duration; // Assuming duration is in nanoseconds
                }

                // Create track via backend API
                try {
                    const response = await axiosApiInstance.post('/api/products/art_tracks/', trackData);

                    // Get the created track with UUID from the response
                    const createdTrack = response.data.track;

                    // Associate track with release
                    if (releaseId && createdTrack) {
                        await axiosApiInstance.put('/api/products/art_tracks/placement', {
                            track_id: createdTrack.id,
                            release_id: releaseId,
                            track_isrc: createdTrack.isrc,
                            volume_number: trackData.volume_number,
                            track_number: trackData.track_number
                        });
                    }

                    newTracks.push(createdTrack || trackData);
                } catch (error) {
                    console.error('Error creating track:', error);
                    message.error(`Failed to create track for ${fileInfo.filename}`);
                }
            }

            // Update the tracks state with the new tracks
            const updatedTracks = [...tracks, ...newTracks];
            setTracks(updatedTracks);

            // Update the form with the updated tracks
            artTracksForm.setFieldsValue({
                art_tracks: updatedTracks
            });
            // Update the atom with the new data
            setArtTracksData({...artTracksData, art_tracks: updatedTracks});

            loadingMessage();
            message.success(`Created ${newTracks.length} new tracks from audio files`);

            // Update volumes state
            updateVolumesFromTracks(updatedTracks);

            // Refetch tracks and placements to ensure we have the latest data
            fetchTracksAndPlacements();

            // Close the preview modal
            setIsCreateTracksPreviewVisible(false);
        } catch (error) {
            loadingMessage();
            console.error('Error creating tracks:', error);
            message.error('Failed to create tracks');
        }
    };

    // Add a new volume
    const handleAddVolume = () => {
        // Find the highest volume number currently in use
        const maxVolumeNumber = Math.max(
            ...volumes.map(volume => volume.volumeNumber),
            0
        );

        // Create a new volume number
        const newVolumeNumber = maxVolumeNumber + 1;

        // Add the new volume to the volumes list
        setVolumes(prev => [...prev, {
            volumeNumber: newVolumeNumber,
            tracks: []
        }]);

        message.success(`Volume ${newVolumeNumber} created successfully`);
    };

    // Remove a volume
    const handleRemoveVolume = (volumeNumber: number) => {
        // Remove the volume from the volumes list
        setVolumes(prev => prev.filter(volume => volume.volumeNumber !== volumeNumber));

        // Update the tracks state to remove any tracks associated with the volume
        // (though there shouldn't be any since we only show the button for empty volumes)
        setTracks(prev => prev.filter(track => track.volume_number !== volumeNumber));

        // Update the form with the updated tracks
        artTracksForm.setFieldsValue({
            art_tracks: tracks.filter(track => track.volume_number !== volumeNumber)
        });
        // Update the atom with the new data
        setArtTracksData({...artTracksData, art_tracks: tracks.filter(track => track.volume_number !== volumeNumber)});

        message.success(`Volume ${volumeNumber} removed successfully`);
    };

    // Update volumes state from tracks
    const updateVolumesFromTracks = (tracks: API.MusicDistribution.ArtTrack[]) => {
        const volumeMap = new Map<number, API.MusicDistribution.ArtTrack[]>();

        // Initialize with existing volume numbers
        volumes.forEach(volume => {
            volumeMap.set(volume.volumeNumber, []);
        });

        // Ensure volume 1 exists
        if (!volumeMap.has(1)) {
            volumeMap.set(1, []);
        }

        // Group tracks by volume
        tracks.forEach(track => {
            const volumeNumber = track.volume_number || 1;
            if (!volumeMap.has(volumeNumber)) {
                volumeMap.set(volumeNumber, []);
            }
            volumeMap.get(volumeNumber)?.push(track);
        });

        // Sort tracks within each volume by track number
        volumeMap.forEach((volumeTracks, volumeNumber) => {
            volumeTracks.sort((a, b) => (a.track_number || 0) - (b.track_number || 0));
        });

        // Convert map to array and sort by volume number
        const volumesArray: VolumeData[] = Array.from(volumeMap.entries())
            .map(([volumeNumber, tracks]) => ({
                volumeNumber,
                tracks
            }))
            .sort((a, b) => a.volumeNumber - b.volumeNumber);
        console.log({volumesArray});
        setVolumes(volumesArray);
    };

    // Handle track changes from a volume
    const handleVolumeTracksChange = (volumeNumber: number, updatedTracks: API.MusicDistribution.ArtTrack[]) => {
        // Update the volume's tracks
        const newVolumes = volumes.map(volume =>
            volume.volumeNumber === volumeNumber
                ? {...volume, tracks: updatedTracks}
                : volume
        );

        setVolumes(newVolumes);

        // Update the tracks state with all tracks from all volumes
        const allTracks = newVolumes.flatMap(volume => volume.tracks);
        setTracks(allTracks);

        // Update the form with all tracks from all volumes
        artTracksForm.setFieldsValue({
            art_tracks: allTracks
        });
        // Update the atom with the new data
        setArtTracksData({...artTracksData, art_tracks: allTracks});

        // Refetch tracks and placements to ensure we have the latest data
        fetchTracksAndPlacements();
    };

    // Get other volumes for a specific volume
    const getOtherVolumes = (volumeNumber: number) => {
        return volumes
            .filter(volume => volume.volumeNumber !== volumeNumber)
            .map(volume => ({
                label: `Volume ${volume.volumeNumber}`,
                value: volume.volumeNumber
            }));
    };

    return (
        <>
            <Space style={{marginBottom: 16}}>
                <Button
                    onClick={handleAutoSync}
                    icon={<SyncOutlined/>}
                    type="primary"
                >
                    Auto Sync Tracks with Files
                </Button>
                <Button
                    onClick={handleCreateTracksFromAudio}
                    icon={<PlusOutlined/>}
                    type="primary"
                >
                    Create Tracks from Audio Files
                </Button>
                <Button
                    onClick={handleAddVolume}
                    icon={<PlusOutlined/>}
                    type="primary"
                >
                    Add Volume
                </Button>
                <Button
                    onClick={fetchTracksAndPlacements}
                    icon={<SyncOutlined/>}
                    type="default"
                >
                    Refresh Tracks
                </Button>
                <span>{audioFiles.length} audio files available</span>
            </Space>

            {/* Hidden form item to store tracks data for StepsForm */}
            <Form.Item
                name="art_tracks"
                hidden
                initialValue={[
                    {
                        ...defaultSelections,
                        volume_number: 1,
                        track_number: 1
                    }
                ]}
            />

            {loading ? (
                <div style={{textAlign: 'center', padding: '50px 0'}}>
                    <Spin size="large" tip="Loading tracks..."/>
                </div>
            ) : (
                <div className="volumes-container">
                    {volumes.map(volume => (
                        <SortableTrackList
                            key={`volume-${volume.volumeNumber}`}
                            volumeNumber={volume.volumeNumber}
                            volumeTracks={volume.tracks}
                            releaseId={id}
                            otherVolumes={getOtherVolumes(volume.volumeNumber)}
                            onTracksChange={(updatedTracks) =>
                                handleVolumeTracksChange(volume.volumeNumber, updatedTracks)
                            }
                            onRemoveVolume={() => handleRemoveVolume(volume.volumeNumber)}
                        />
                    ))}
                </div>
            )}
            {/* Auto-sync Preview Modal */}
            <Modal
                title="Auto Sync Preview"
                open={isPreviewModalVisible}
                onCancel={() => setIsPreviewModalVisible(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsPreviewModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        onClick={applyAutoSync}
                        disabled={previewData.matched.length === 0}
                    >
                        Apply Auto Sync
                    </Button>
                ]}
            >
                <div style={{marginBottom: 16}}>
                    <Typography.Title level={5}>
                        {previewData.matched.length} of {tracks.length} tracks will be matched with audio files
                    </Typography.Title>
                    <Typography.Paragraph>
                        Please review the matches below before confirming.
                    </Typography.Paragraph>
                </div>

                {previewData.matched.length > 0 && (
                    <>
                        <Typography.Title level={5} style={{color: '#52c41a'}}>
                            <CheckCircleOutlined/> Tracks that will be matched:
                        </Typography.Title>
                        <Table
                            dataSource={previewData.matched}
                            rowKey={(record) => record.track.id}
                            pagination={false}
                            size="small"
                            style={{marginBottom: 24}}
                            columns={[
                                {
                                    title: 'Track',
                                    dataIndex: ['track', 'title'],
                                    key: 'title',
                                    render: (text, record) => (
                                        <span>{record.track.title || `Track ${record.track.track_number}`}</span>
                                    )
                                },
                                {
                                    title: 'Will be matched with',
                                    dataIndex: 'filename',
                                    key: 'filename'
                                }
                            ]}
                        />
                    </>
                )}

                {previewData.unmatched.length > 0 && (
                    <>
                        <Typography.Title level={5} style={{color: '#ff4d4f'}}>
                            <CloseCircleOutlined/> Tracks that could not be matched:
                        </Typography.Title>
                        <Table
                            dataSource={previewData.unmatched}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Track',
                                    dataIndex: 'title',
                                    key: 'title',
                                    render: (text, record) => (
                                        <span>{record.title || `Track ${record.track_number}`}</span>
                                    )
                                },
                                {
                                    title: 'Current Filename',
                                    dataIndex: 'filename',
                                    key: 'filename',
                                    render: (text) => text || 'None'
                                }
                            ]}
                        />
                    </>
                )}
            </Modal>

            {/* Create Tracks Preview Modal */}
            <Modal
                title="Create Tracks Preview"
                open={isCreateTracksPreviewVisible}
                onCancel={() => setIsCreateTracksPreviewVisible(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setIsCreateTracksPreviewVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        onClick={applyCreateTracks}
                        disabled={createTracksPreviewData.filesToCreate.length === 0}
                    >
                        Create Tracks
                    </Button>
                ]}
            >
                <div style={{marginBottom: 16}}>
                    <Typography.Title level={5}>
                        {createTracksPreviewData.filesToCreate.length} tracks will be created from audio files
                    </Typography.Title>
                    <Typography.Paragraph>
                        Please review the tracks to be created below before confirming.
                    </Typography.Paragraph>
                </div>

                {createTracksPreviewData.filesToCreate.length > 0 && (
                    <>
                        <Typography.Title level={5} style={{color: '#52c41a'}}>
                            <CheckCircleOutlined/> Tracks that will be created:
                        </Typography.Title>
                        <Table
                            dataSource={createTracksPreviewData.filesToCreate}
                            rowKey="filename"
                            pagination={false}
                            size="small"
                            style={{marginBottom: 24}}
                            columns={[
                                {
                                    title: 'Audio File',
                                    dataIndex: 'filename',
                                    key: 'filename'
                                },
                                {
                                    title: 'Track Title',
                                    dataIndex: 'title',
                                    key: 'title'
                                }
                            ]}
                        />
                    </>
                )}
            </Modal>
        </>
    );
};

export default StepsFormSecondPage;
